import Conviva from '@convivainc/conviva-js-coresdk';
import { ClientType, ColorSpace } from '@mlbtv-clients/services';
import { WIRELESS } from 'analytics/conviva/constants';
import { releaseConvivaAnalyticsSingleton } from 'analytics/conviva/hooks/useConviva/convivaAnalyticsSingleton';
import { EASTERN_TZ } from 'constants/date';
import { MODEL_YEAR_UNKNOWN } from 'constants/platforms';
import { ROUTES } from 'constants/screens';
import { US_COUNTRY_CODE } from 'constants/userInfo';
import { ModelTier } from 'hooks/useDeviceModelTier/types';
import { getModelTier } from 'hooks/useDeviceModelTier/utils';
import { NavigateFunction } from 'react-router-dom';
import { EPGErrors } from 'services/epg/constants';
import fallback from 'store/config/fallback.json';
import { IFA } from 'store/deviceSettings';
import { v4 as uuidv4 } from 'uuid';

const { EPG_SERVICE_ERROR } = EPGErrors;

interface DeviceMetadata {
  deviceBrand: string;
  deviceManufacturer: string;
  deviceModel: string;
  operatingSystemName: string;
}

const {
  Constants: { DeviceCategory, DeviceMetadata, DeviceType },
} = Conviva;

const {
  BRAND,
  CATEGORY,
  MANUFACTURER,
  MODEL,
  OS_NAME,
  SCREEN_RESOLUTION_HEIGHT,
  SCREEN_RESOLUTION_WIDTH,
  TYPE,
} = DeviceMetadata;

export const getClientType = async () => ClientType.VIZIO;

export const getDeviceOSVersion = async (defaultOS: string) => {
  try {
    const osVersion = await new Promise((resolve) => {
      window.VIZIO.getFirmwareVersion((version: string) => resolve(version));
    });
    return osVersion;
  } catch (e) {
    console.log('error loading firmware, using fallback', e);
  }
  return defaultOS;
};

// Generated once per app session so ad requests keep a stable rdid when no IFA is available.
let sessionDuid = '';
const getSessionDuid = () => {
  if (!sessionDuid) {
    sessionDuid = uuidv4();
  }
  return sessionDuid;
};

export const getDeviceAdParams = (adParams: Record<string, string>, ifaParams?: IFA) => {
  return {
    ...adParams,
    is_lat: ifaParams?.LMT === 1,
    rdid: ifaParams?.IFA || getSessionDuid(),
  };
};

export const getDeviceCaptionStyle = async () => ``;

export const getDeviceCountryCode = async () => US_COUNTRY_CODE;

export const getDeviceConnectionType = async () => WIRELESS;

export const getDeviceExitApp = () => {
  releaseConvivaAnalyticsSingleton();
  window.VIZIO?.exitApplication();
};

export const getDeviceExitConfirmation = (navigate?: NavigateFunction) => {
  if (navigate) navigate(ROUTES.EXIT);
};

export const getDeviceId = async (deviceId: string) => {
  try {
    const vizioId = await new Promise((resolve) => {
      window.VIZIO.getDeviceId((id: string) => resolve(id));
    });
    return vizioId;
  } catch (e) {
    return deviceId;
  }
};

export const getDeviceIdType = () => 'dpid';

export const getDeviceKeyboardIsOpen = async () => false;

export const getDeviceMetadata = async (deviceMetadata: DeviceMetadata) => {
  const { deviceBrand, deviceManufacturer, deviceModel, operatingSystemName } = deviceMetadata;

  const deviceInfo = {
    firmwareVersion: await getDeviceOSVersion(operatingSystemName),
    model: window.VIZIO?.deviceModel,
  };

  return {
    [BRAND]: deviceBrand,
    [CATEGORY]: DeviceCategory.SMART_TV,
    [MANUFACTURER]: deviceManufacturer,
    [MODEL]: deviceInfo.model || deviceModel,
    [OS_NAME]: deviceInfo.firmwareVersion,
    [SCREEN_RESOLUTION_HEIGHT]: window.screen.height,
    [SCREEN_RESOLUTION_WIDTH]: window.screen.width,
    [TYPE]: DeviceType.SMARTTV,
  };
};

export const getDeviceModelTier = async (modelTiers: Record<string, ModelTier>) =>
  getModelTier(modelTiers, '');

// Vizio Documentation
// https://docs.developer.vizio.com/docs/vizio-tv-model-information
export const getDeviceModelYear = async () => {
  const defaultYear = MODEL_YEAR_UNKNOWN;
  const model = window.VIZIO?.deviceModel;

  if (!model) {
    return defaultYear;
  }

  const modelYearChar = model.split('-')[1]?.charAt(0);

  if (!modelYearChar || modelYearChar < 'E' || modelYearChar > 'N') {
    return defaultYear;
  }

  const baseYear = 2017;
  const year = baseYear + (modelYearChar.charCodeAt(0) - 'E'.charCodeAt(0));

  return year.toString();
};

export const getDevicePlaybackCapabilities = async () => {
  return new Promise((resolve) => {
    window.VIZIO.getDeviceHDRCapabilities((capabilities: string[]) => {
      const includesHDR = capabilities.includes('HDR10');
      resolve({ colorSpace: [includesHDR ? ColorSpace.HDR : ColorSpace.SDR] });
    });
  });
};

export const getDeviceModel = async (defaultValue: string) =>
  window.VIZIO?.deviceModel ?? defaultValue;

export const getDeviceSKU = async (defaultValue: string) => defaultValue;

export const getDeviceTimeZone = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (!timeZone) {
    throw new Error(`${EPG_SERVICE_ERROR} Time zone not found. Falling back to ${EASTERN_TZ}`);
  }

  return timeZone;
};

export const getDeviceHasSpinner = async (
  hasSpinner: typeof fallback.featureFlags.animations.hasSpinner,
) => !hasSpinner.hasAllModelsDisabled;

export const getDeviceTTSEnabled = () => false;
